import { ApiClientError } from '../api/client'

/**
 * Error codes -> what to tell the user.
 *
 * The distinction this component exists for is `POOL_STATE_STALE` versus `NO_ROUTE`. The
 * API keeps them apart deliberately and it would be undone here by one shared "no route
 * found" string. They mean opposite things:
 *
 *   NO_ROUTE          the engine quoted this pair and nothing cleared. About the pair.
 *   POOL_STATE_STALE  the engine refused to rank pools on data this old, so it never
 *                     quoted. Says nothing about the pair, and the fix is operational —
 *                     WP3's refresher has stopped or cannot reach the node.
 *
 * Collapsed together, a stopped refresher reads as an unroutable token, and the next hours
 * go into the routing engine instead of into the process that died. Same reason
 * `NODE_UNAVAILABLE` gets its own wording: a route that reverted is information about the
 * pair, a node that would not answer is the absence of information.
 */

type Copy = { title: string; body: string; tone: 'warn' | 'error'; retry: boolean }

function copyFor(error: ApiClientError): Copy {
  switch (error.code) {
    case 'NO_ROUTE':
      return {
        title: 'No route for this pair and size',
        body: 'The engine quoted every candidate route and none of them clears. A smaller amount may.',
        tone: 'warn',
        retry: false,
      }
    case 'POOL_STATE_STALE':
      return {
        title: 'Pool data is stale — this is not about your pair',
        body:
          'The routing engine will not rank pools on state this old, so it did not quote at all. ' +
          "The indexer's refresher has stopped or cannot reach the node. Your pair may be perfectly routable.",
        tone: 'error',
        retry: true,
      }
    case 'NODE_UNAVAILABLE':
      return {
        title: 'The Flare node would not answer',
        body: 'Nothing is known about this trade — this is the absence of an answer, not a negative one.',
        tone: 'error',
        retry: true,
      }
    case 'UNKNOWN_TOKEN':
      return {
        title: `${(error.detail.side as string) ?? 'A token'} is not routable`,
        body:
          'Either the indexer has never seen it, or it was excluded by token hygiene — fee-on-transfer ' +
          'and rebasing tokens silently produce wrong amounts, so they are left out rather than risked.',
        tone: 'warn',
        retry: false,
      }
    case 'RATE_LIMITED':
      return {
        title: 'Too many quotes, too fast',
        body: 'The API shed this request to keep the node ahead of live traffic. It will recover in a second.',
        tone: 'warn',
        retry: true,
      }
    case 'AMOUNT_INVALID':
    case 'SAME_TOKEN':
    case 'BAD_REQUEST':
      return { title: 'That request cannot be quoted', body: error.message, tone: 'warn', retry: false }
    case 'NETWORK':
      return {
        title: 'Cannot reach the quote API',
        body:
          'No response at all, so there is no API error code to read. In development, check that ' +
          '`pnpm --filter @sparkdex/api serve` is running and that the Vite proxy points at it.',
        tone: 'error',
        retry: true,
      }
    default:
      return { title: 'The quote failed', body: error.message, tone: 'error', retry: true }
  }
}

export function QuoteError({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  const copy =
    error instanceof ApiClientError
      ? copyFor(error)
      : { title: 'Something went wrong', body: error.message, tone: 'error' as const, retry: true }

  return (
    <div className={`notice notice-${copy.tone}`}>
      <div className="notice-title">{copy.title}</div>
      <p className="notice-body">{copy.body}</p>
      {error instanceof ApiClientError && (
        <div className="notice-meta">
          <code>{error.code}</code>
          <span>{error.message}</span>
        </div>
      )}
      {copy.retry && onRetry && (
        <button type="button" className="btn btn-ghost btn-small" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  )
}
